// Parent tasks orchestrate their subtasks (D87, docs/12): what a task says about its children,
// its plan, its pause and its waits (a VPN sign-in among them since S153). Written by hand against the docs/12 contract until S148's
// routes reach the generated schema; every field is read defensively so an older API (or a
// task without children) parses to "nothing to show".
import { requestJson } from "../../api/http";

type Json = Record<string, unknown>;
const obj = (x: unknown): Json => (typeof x === "object" && x !== null && !Array.isArray(x) ? (x as Json) : {});
const str = (x: unknown): string => (typeof x === "string" ? x : "");
const optStr = (x: unknown): string | null => (typeof x === "string" && x !== "" ? x : null);
const num = (x: unknown): number => (typeof x === "number" && Number.isFinite(x) ? x : 0);
const list = (x: unknown): unknown[] => (Array.isArray(x) ? x : []);

/**
 * What a task waits on (S141, D87, S153). `subtasks` and `parent` carry no `since`; `vpn` is a
 * task held, before its next step, until its environment's VPN is signed in (still `running`).
 */
export type WaitingOn =
  | { kind: "questions"; count: number; since: number }
  | { kind: "approval"; since: number }
  | { kind: "vpn"; environment_id: string; since: number }
  | { kind: "subtasks"; running: number; waiting: number; failed: number; done: number; total: number }
  | { kind: "parent"; task_id: string; ref: string };

/** The API counts a parent's waiting and failed subtasks; an early draft listed them. */
const count = (x: unknown): number => (Array.isArray(x) ? x.length : num(x));

export function parseWaitingOn(x: unknown): WaitingOn | null {
  const w = obj(x);
  switch (w.kind) {
    case "questions":
      return { kind: "questions", count: num(w.count), since: num(w.since) };
    case "approval":
      return { kind: "approval", since: num(w.since) };
    case "vpn":
      return { kind: "vpn", environment_id: str(w.environment_id), since: num(w.since) };
    case "subtasks":
      return {
        kind: "subtasks",
        running: num(w.running),
        waiting: count(w.waiting),
        failed: count(w.failed),
        done: num(w.done),
        total: num(w.total),
      };
    case "parent":
      return { kind: "parent", task_id: str(w.task_id), ref: str(w.ref) || str(w.task_id) };
    default:
      return null;
  }
}

/** A child as its parent's task lists it. */
export interface Child {
  id: string;
  ref: string;
  title: string;
  status: string;
  current_node: string | null;
  waiting_on: WaitingOn | null;
}

/** One subtask of the plan: where it is built and after which others. */
export interface PlanItem {
  task: string;
  repository: string;
  branch: string;
  after: string[];
  base: string | null;
  notes: string;
  done: boolean;
}

export interface Plan {
  version: number;
  subtasks: PlanItem[];
  summary: string;
  created_at: number;
}

/** The D87 fields of a task, all defaulted when absent. */
export interface Orchestration {
  paused: boolean;
  base_branch: string | null;
  held_by_parent: boolean;
  children: Child[];
  plan: Plan | null;
}

function parseChild(x: unknown): Child {
  const c = obj(x);
  return {
    id: str(c.id),
    ref: str(c.ref) || str(c.id),
    title: str(c.title),
    status: str(c.status),
    current_node: optStr(c.current_node),
    waiting_on: parseWaitingOn(c.waiting_on),
  };
}

function parsePlan(x: unknown): Plan | null {
  const p = obj(x);
  if (!("plan" in p)) return null;
  const body = obj(p.plan);
  return {
    version: num(p.version),
    summary: str(body.summary),
    created_at: num(p.created_at),
    subtasks: list(body.subtasks).map((s) => {
      const i = obj(s);
      return {
        task: str(i.task),
        repository: str(i.repository),
        branch: str(i.branch),
        after: list(i.after).filter((a): a is string => typeof a === "string"),
        base: optStr(i.base),
        notes: str(i.notes),
        done: i.done === true,
      };
    }),
  };
}

export function parseOrchestration(task: unknown): Orchestration {
  const t = obj(task);
  return {
    paused: t.paused === true,
    base_branch: optStr(t.base_branch),
    held_by_parent: t.held_by_parent === true,
    children: list(t.children).map(parseChild),
    plan: parsePlan(t.plan),
  };
}

/** Stop starting new steps (and, for a parent, its unfinished subtasks') until resumed. */
export const pauseTask = (id: string) => requestJson("POST", `/api/tasks/${encodeURIComponent(id)}/pause`);
export const resumeTask = (id: string) => requestJson("POST", `/api/tasks/${encodeURIComponent(id)}/resume`);
